import { Component } from '@angular/core';
import { PanAppState, PlatformService, jwtDecode } from '@panamax/app-state';
import { Observable, combineLatest, filter, take } from 'rxjs';
import { DeliveriesViewModel } from './models/deliveries-view-model';
import { QuantityUpdate } from '@shared/models/quantity-update';
import { OrderItemService } from '@shared/services/order-item.service';
import { DeliveriesService } from './services/deliveries.service';
import {
  MOST_POPULAR_EXPERIENCE,
  MOST_VIEWED_EXPERIENCE,
  REPLENISHMENT_EXPERIENCE,
  SEARCHED_ALSO_PURCHASED_EXPERIENCE,
  TRENDING_ITEMS_EXPERIENCE,
} from '@shared/constants/merchandising-constants';
import { ProductService } from '@shared/services/product/product.service';
import { LAST_SEARCH_TERM } from '@search/shared/constants/search-config';
import { PdpLnkSrc } from '@shared/constants/pdp-lnksrc.enum';

@Component({
  selector: 'app-deliveries',
  templateUrl: './deliveries.page.html',
  styleUrls: ['./deliveries.page.scss'],
})
export class DeliveriesPage {
  constructor(
    protected panAppState: PanAppState,
    readonly platformService: PlatformService,
    private orderItemService: OrderItemService,
    private deliveriesService: DeliveriesService,
    private productService: ProductService,
  ) {}

  lastSearchTerm = 'cheese';
  linkSource = PdpLnkSrc.deliveries;
  experienceToTitleMap = {
    [MOST_VIEWED_EXPERIENCE]: 'Most Viewed',
    [TRENDING_ITEMS_EXPERIENCE]: 'Trending Items',
    [SEARCHED_ALSO_PURCHASED_EXPERIENCE]: `Searched and Also Purchased (Default: ${this.lastSearchTerm})`,
    [REPLENISHMENT_EXPERIENCE]: 'Replenishment',
    [MOST_POPULAR_EXPERIENCE]: 'Most Popular',
  };

  deliveriesViewModel$: Observable<DeliveriesViewModel>;
  isInternalUser: boolean;
  isUserCCA: boolean;

  ionViewWillEnter() {
    this.panAppState.setPageTitle('Deliveries');

    const isMerchandisingStateReady$ = this.deliveriesService.isMerchandisingStateReady$();

    combineLatest([isMerchandisingStateReady$, this.panAppState?.accessToken$])
      .pipe(
        filter(
          ([isMerchandisingStateReady, token]) =>
            isMerchandisingStateReady && !!token,
        ),
        take(1),
      )
      .subscribe(([isMerchandisingStateReady, token]) => {
        this.setUserClaims(token);
        if (this.isInternalUser || this.isUserCCA) {
          const lastSearchTerm = sessionStorage.getItem(LAST_SEARCH_TERM);
          if (!!lastSearchTerm) {
            this.lastSearchTerm = lastSearchTerm;
            this.experienceToTitleMap[
              SEARCHED_ALSO_PURCHASED_EXPERIENCE
            ] = `Searched and Also Purchased (${this.lastSearchTerm})`;
          }

          const experienceRequest = this.deliveriesService.getExperiencesRequest(
            this.lastSearchTerm,
          );
          this.deliveriesService.fetchMerchandising(experienceRequest);
        }
      });

    const experienceRequest = this.deliveriesService.getExperiencesRequest(
      this.lastSearchTerm,
    );

    this.deliveriesService
      .getDeliveriesProductNumbers$(experienceRequest)
      .pipe(
        filter(productNumbers => productNumbers?.length > 0),
        take(1),
      )
      .subscribe(productNumbers => {
        this.productService.loadProducts(productNumbers);
      });
    this.deliveriesViewModel$ = this.deliveriesService.getDeliveriesViewModel$(
      experienceRequest,
    );
  }

  trackByIndex = (index: number, id: any) => index;

  onQuantityUpdate(quantityUpdate: QuantityUpdate) {
    this.orderItemService.changeQuantity(
      quantityUpdate.productNumber,
      quantityUpdate.quantity,
      quantityUpdate.isEaches,
    );
  }

  setUserClaims(token: string): void {
    const decoded = jwtDecode(token);
    if (decoded) {
      this.isInternalUser = decoded?.['usf-claims']?.userType === 'corp-ad';
      this.isUserCCA = decoded?.['usf-claims']?.orderTakerRole === 'CCA';
    }
  }
}
